import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CalendarDays, Users, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";
import { it, enUS, de, pt, hu } from "date-fns/locale";
import { RESTAURANT } from "../../lib/restaurantData";
import { useT } from "../../lib/i18n";
import { PhoneInput } from "./PhoneInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate 5-minute slots matching backend TURNS configuration.
// Note: the actual list of slots is always driven by /api/availability, but this
// static fallback is used before the API call resolves and to keep tests deterministic.
const _genSlots = (sh, sm, eh, em) => {
  const out = [];
  let t = sh * 60 + sm;
  const end = eh * 60 + em;
  while (t <= end) {
    out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
    t += 5;
  }
  return out;
};
const TIMES = [
  ..._genSlots(11, 30, 15, 0),
  ..._genSlots(18, 30, 19, 25),
  ..._genSlots(19, 30, 20, 25),
  ..._genSlots(20, 30, 21, 25),
  ..._genSlots(21, 30, 22, 30),
];

const LOCALES = { it, en: enUS, de, pt, hu };

export const Reservation = () => {
  const { t, lang } = useT();
  const locale = LOCALES[lang] || it;

  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [people, setPeople] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [availability, setAvailability] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [sameDayCutoff, setSameDayCutoff] = useState(false);
  const [autoMax, setAutoMax] = useState(5);

  // Fetch availability when date changes
  useEffect(() => {
    if (!date) { setAvailability({}); setSameDayCutoff(false); return; }
    const iso = format(date, "yyyy-MM-dd");
    setLoadingAvail(true);
    axios.get(`${API}/availability`, { params: { date: iso } })
      .then((res) => {
        setAvailability(res.data.slots || {});
        setSameDayCutoff(!!res.data.same_day_cutoff);
        if (res.data.auto_accept_max_people) setAutoMax(res.data.auto_accept_max_people);
      })
      .catch(() => { setAvailability({}); setSameDayCutoff(false); })
      .finally(() => setLoadingAvail(false));
    setTime("");
  }, [date]);

  const peopleNum = parseInt(people, 10) || 0;
  const isGroup = peopleNum > autoMax;
  const isCutoff = sameDayCutoff;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time || !people || !name || !phone) {
      toast.error(t("res.toastErr"));
      return;
    }
    setLoading(true);
    try {
      const isoDate = format(date, "yyyy-MM-dd");
      const payload = {
        name, phone, email: email || null, date: isoDate, time,
        people: parseInt(people, 10), notes,
      };
      await axios.post(`${API}/reservations`, payload);
      toast.success(t("res.toastOk"));

      const msg =
        `${t("whatsappResIntro")}%0A%0A` +
        `👤 ${t("whatsappResName")}: ${name}%0A` +
        `📞 ${t("whatsappResPhone")}: ${phone}%0A` +
        `📅 ${t("whatsappResDate")}: ${format(date, "dd/MM/yyyy")}%0A` +
        `🕒 ${t("whatsappResTime")}: ${time}%0A` +
        `👥 ${t("whatsappResPeople")}: ${people}` +
        (notes ? `%0A📝 ${t("whatsappResNotes")}: ${notes}` : "") +
        `%0A%0A⚠️ ${encodeURIComponent(t("res.policyBody"))}`;
      window.open(`https://wa.me/${RESTAURANT.whatsapp}?text=${msg}`, "_blank");

      setDate(undefined); setTime(""); setPeople(""); setName(""); setPhone(""); setEmail(""); setNotes("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        const code = (typeof detail === "object") ? detail?.code : null;
        const msg = (typeof detail === "object" && detail?.message) ? detail.message :
                    (code === "same_day_cutoff" ? t("res.toastCutoff") : t("res.toastFull"));
        toast.error(msg);
        if (date) {
          const iso = format(date, "yyyy-MM-dd");
          try {
            const res = await axios.get(`${API}/availability`, { params: { date: iso } });
            setAvailability(res.data.slots || {});
            setSameDayCutoff(!!res.data.same_day_cutoff);
          } catch {}
        }
      } else {
        toast.error(t("res.toastFail"));
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="prenota" data-testid="reservation-section" className="py-20 sm:py-28 bg-[#F5EFE4]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-2">
            <span className="font-display text-xs tracking-[0.3em] text-[#B59410] font-semibold">
              {t("res.eyebrow")}
            </span>
            <h2 className="mt-4 font-script text-6xl sm:text-7xl lg:text-8xl text-[#1E3A5D] leading-[1]">
              {t("res.title")}
            </h2>
            <p className="mt-5 text-[#1E3A5D]/75 text-base sm:text-lg leading-relaxed font-serif-body italic">
              {t("res.desc")}
            </p>

            <div className="mt-8 space-y-3 text-[#1E3A5D] font-serif-body">
              <p className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#B59410]" />
                {t("res.info1")}
              </p>
              <p className="flex items-center gap-3">
                <Users className="w-4 h-4 text-[#B59410]" />
                {t("res.info2")}
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-testid="reservation-form"
            className="lg:col-span-3 bg-white rounded-3xl border border-[#1E3A5D]/15 shadow-sm p-6 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="r-name" className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.name")}</Label>
                <Input id="r-name" data-testid="reservation-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="r-phone" className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.phone")}</Label>
                <div className="mt-2">
                  <PhoneInput value={phone} onChange={setPhone} lang={lang} testId="reservation-phone" />
                </div>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="r-email" className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.email")}</Label>
                <Input id="r-email" type="email" data-testid="reservation-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
              </div>

              <div>
                <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.date")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="reservation-date-trigger" className="mt-2 w-full justify-start font-normal text-left">
                      <CalendarDays className="w-4 h-4 mr-2 text-[#B59410]" />
                      {date ? format(date, "EEEE d MMMM yyyy", { locale }) : t("res.datePlaceholder")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      locale={locale}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.time")}</Label>
                <Select value={time} onValueChange={setTime} disabled={!date || loadingAvail}>
                  <SelectTrigger data-testid="reservation-time-select" className="mt-2">
                    <Clock className="w-4 h-4 mr-2 text-[#B59410]" />
                    <SelectValue placeholder={!date ? t("res.datePlaceholder") : (loadingAvail ? "..." : t("res.timePlaceholder"))} />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMES.map((v) => {
                      const slot = availability[v];
                      const isFull = slot && !slot.available;
                      const remaining = slot?.remaining_people ?? null;
                      const low = !isFull && remaining !== null && remaining > 0 && remaining <= 5;
                      return (
                        <SelectItem
                          key={v}
                          value={v}
                          disabled={isFull}
                          data-testid={`time-option-${v}`}
                          className={isFull ? "opacity-50" : ""}
                        >
                          <span className="flex items-center gap-2 w-full">
                            <span className={isFull ? "line-through text-stone-400" : ""}>{v}</span>
                            {isFull && (
                              <span className="ml-auto text-[10px] uppercase tracking-wider text-rose-600 font-semibold">
                                · {t("res.slotFull")}
                              </span>
                            )}
                            {low && (
                              <span className="ml-auto text-[10px] tracking-wider text-[#B59410] font-semibold">
                                {remaining} {t("res.slotRemaining")}
                              </span>
                            )}
                          </span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.people")}</Label>
                <Select value={people} onValueChange={setPeople}>
                  <SelectTrigger data-testid="reservation-people-select" className="mt-2">
                    <Users className="w-4 h-4 mr-2 text-[#B59410]" />
                    <SelectValue placeholder={t("res.peoplePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                      <SelectItem key={n} value={String(n)} data-testid={`people-option-${n}`}>
                        {n} {n === 1 ? t("res.personOne") : t("res.personMany")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="r-notes" className="font-display text-xs text-[#1E3A5D] tracking-[0.16em] font-semibold">{t("res.notes")}</Label>
                <Textarea id="r-notes" data-testid="reservation-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="mt-2" placeholder={t("res.notesPh")} />
              </div>
            </div>

            <div
              data-testid="reservation-policy-notice"
              className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 flex gap-3"
            >
              <span className="text-amber-600 text-xl leading-none font-bold shrink-0">!</span>
              <div>
                <p className="font-display text-xs text-amber-900 tracking-[0.14em] font-semibold uppercase">
                  {t("res.policyTitle")}
                </p>
                <p className="mt-1.5 text-[#1E3A5D] text-sm leading-relaxed font-serif-body">
                  {t("res.policyBody")}
                </p>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || isCutoff}
              data-testid="reservation-submit-btn"
              className={`mt-8 w-full sm:w-auto font-display text-xs tracking-[0.22em] rounded-full text-white px-10 py-6 font-semibold transition-transform hover:-translate-y-0.5 hover:shadow-lg ${
                isGroup ? "bg-[#1E3A5D] hover:bg-[#152a44]" : "bg-[#B59410] hover:bg-[#8E7308]"
              }`}
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isGroup ? t("res.groupSubmit") : t("res.submit")}
            </Button>
            <p className="mt-3 text-[#1E3A5D]/60 text-xs font-serif-body italic">
              {t("res.submitSub")}
            </p>
            {isGroup && (
              <div
                data-testid="group-notice"
                className="mt-4 rounded-xl border border-[#B59410]/40 bg-[#B59410]/10 p-4 text-[#1E3A5D] text-sm font-serif-body flex gap-3"
              >
                <span className="text-[#B59410] text-lg leading-none">!</span>
                <span>{t("res.groupNote")}</span>
              </div>
            )}
            {isCutoff && (
              <div
                data-testid="cutoff-notice"
                className="mt-4 rounded-xl border border-rose-300 bg-rose-50 p-4 text-[#1E3A5D] text-sm font-serif-body"
              >
                {t("res.cutoffBanner")}{" "}
                <a href={RESTAURANT.phoneHref} className="font-semibold text-[#B59410] hover:text-[#8E7308]">
                  {RESTAURANT.phone}
                </a>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
